/**
 * A market distribution: how many, and how many of those subscribed.
 *
 * BOTH COLUMNS, always. The gap between «who we meet» and «who buys» is the
 * interesting part, and splitting them across two cards makes a reader hold
 * one in their head while they look at the other.
 *
 * Drawn with the tokenized BarChart, which is flex rows rather than a charting
 * library — the only chart this page needs is a horizontal bar, and a library
 * would bring its own colours, font sizing and opinion about RTL to override.
 */
import { BarChart, Card } from '@meshhub/design-tokens/ui';
import { Info } from 'lucide-react';
import React, { useState } from 'react';
import { formatCount } from '../../lib/format.js';
import { S } from '../../lib/strings.js';

export function DistributionCard({ metric, onDrillDown }) {
  const [showInfo, setShowInfo] = useState(false);
  const { definition: d } = metric;

  const data = metric.rows.map((row) => ({
    label: row.label,
    value: row.values[0] ?? 0,
    // The subscriber subset, rendered as the secondary number rather than a
    // second bar: two bars per row doubles the height of the card and halves
    // how much of it fits on a screen.
    secondary: row.values[1] ?? 0,
    key: row.key,
  }));

  return (
    <Card className="flex flex-col gap-2 p-4">
      <div className="flex items-start justify-between gap-2">
        <h3 className="text-sm font-medium text-text">{d.titleFa}</h3>
        <button
          type="button"
          onClick={() => setShowInfo((v) => !v)}
          aria-expanded={showInfo}
          aria-label={`${S.dashboard.definition}: ${d.titleFa}`}
          className="shrink-0 rounded p-1 text-text-muted hover:bg-surface-hover hover:text-text"
        >
          <Info size={14} aria-hidden="true" />
        </button>
      </div>

      <BarChart
        data={data}
        valueLabel={metric.buckets[0]}
        formatValue={(v, row) =>
          `${formatCount(v)}${row?.secondary ? ` / ${formatCount(row.secondary)}` : ''}`
        }
        emptyLabel={S.dashboard.noData}
        onSelect={onDrillDown ? (row) => onDrillDown(metric, row.key) : undefined}
      />

      {showInfo && (
        <dl className="flex flex-col gap-2 border-t border-border pt-2 text-xs">
          <div>
            <dt className="font-medium text-text">{S.dashboard.formula}</dt>
            <dd className="text-text-muted">{d.formula}</dd>
          </div>
          {d.caveat && (
            <div>
              <dt className="font-medium text-warning">{S.dashboard.caveat}</dt>
              <dd className="text-text">{d.caveat}</dd>
            </div>
          )}
        </dl>
      )}
    </Card>
  );
}
